import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, AlertCircle, Loader2 } from 'lucide-react';
import logoUrl from '../../src/styles/logo.png';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { GoogleButton } from '../shared/GoogleButton';
import EmailCodeForm from '../shared/EmailCodeForm';
import { handOffToApp } from '../../lib/authHandoff';

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();

  const [orgName, setOrgName] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid' | 'accepting' | 'done'>('loading');
  const [error, setError] = useState('');
  // 'code' : saisie du code reçu par email, seul canal fiable quand la
  // messagerie du destinataire brûle les liens à usage unique.
  const [mode, setMode] = useState<'choose' | 'code'>('choose');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    supabase
      .rpc('get_invite_info', { p_token: token })
      .then(async ({ data, error }) => {
        const d = data as any;
        if (error || !data || d.error) {
          // Invite already consumed — check if user is authenticated (inviteUserByEmail flow)
          const { data: { session: activeSession } } = await supabase.auth.getSession();
          if (activeSession) {
            navigate('/onboarding', { replace: true });
          } else {
            setStatus('invalid');
          }
        } else {
          setOrgName(d.org_name);
          setInviteEmail(d.email);
          setStatus('valid');
        }
      });
  }, [token, navigate]);

  /** La session en cours voit-elle déjà une organisation ? La RLS ne laisse
   *  lire que celle dont on est membre : une ligne suffit à conclure. */
  const hasOrgMembership = async (): Promise<boolean> => {
    const { count } = await supabase
      .from('organization_members')
      .select('org_id', { count: 'exact', head: true });
    return (count ?? 0) > 0;
  };

  /** Rattachement à l'organisation, session déjà établie. */
  const acceptWithSession = async () => {
    if (!token) return;
    setStatus('accepting');
    const { data, error } = await supabase.rpc('accept_invite', { p_token: token });
    const d = data as any;
    const failure = error ? error.message : (d?.error as string | undefined);

    if (failure) {
      // Session ouverte sur un AUTRE compte : l'invitation vise quelqu'un
      // d'autre et personne n'a rejoint quoi que ce soit. Le test du
      // 2026-08-20 affichait « Bienvenue dans l'équipe » dans ce cas — un faux
      // succès, l'invitation restait en attente et aucun compte n'était créé.
      const wrongAccount = failure.includes('ne correspond pas');
      const alreadyMember = failure.includes('déjà membre');
      // Le trigger de confirmation a pu rattacher la personne et consommer le
      // jeton une fraction de seconde avant cet appel : là, c'est bien un
      // succès arrivé par un autre chemin.
      const joinedByTrigger =
        !wrongAccount && !alreadyMember && (await hasOrgMembership());

      if (!alreadyMember && !joinedByTrigger) {
        setError(
          wrongAccount && inviteEmail
            ? `Vous êtes connecté avec une autre adresse. Cette invitation est destinée à ${inviteEmail} : déconnectez-vous, puis rouvrez ce lien.`
            : failure
        );
        setStatus('valid');
        setMode('choose');
        return;
      }
    }

    setStatus('done');
    // Vers l'onboarding et non l'accueil : il réclame prénom et nom quand ils
    // manquent, ce qui est le cas de tout invité, puis renvoie sur /home.
    setTimeout(() => handOffToApp('/onboarding'), 1500);
  };

  /** Bouton « Rejoindre » : réservé à une session déjà ouverte. Sans session, on
   *  passe par le code ou par Google, jamais par un formulaire d'inscription. */
  const handleAccept = async () => {
    if (!token || !session) return;
    await acceptWithSession();
  };

  const handleOAuthWithInvite = () => {
    if (token) localStorage.setItem('invite_token', token);
  };

  /** Ferme la session en cours sans quitter la page : on revient sur cette
   *  même invitation, déconnecté, prêt à la rejoindre avec la bonne adresse. */
  const handleSwitchAccount = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const emailMismatch =
    !!session?.user?.email &&
    !!inviteEmail &&
    session.user.email.toLowerCase() !== inviteEmail.toLowerCase();

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 size={32} className="text-amber-500 animate-spin" />
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
            <AlertCircle size={40} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-white mb-2">Invitation déjà utilisée</h2>
            <p className="text-sm text-slate-400 mb-6">
              Ce lien a déjà servi, ou il a expiré. Si vous avez reçu une invitation par email,
              la protection de votre messagerie a pu ouvrir le lien avant vous : votre compte
              existe déjà, connectez-vous avec un code reçu par email.
            </p>
            <Link
              to="/login?code=1"
              className="block w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-2.5 rounded-lg transition-colors text-sm mb-4"
            >
              Recevoir un code par email
            </Link>
            <Link to="/login" className="text-amber-500 hover:text-amber-400 text-sm font-medium transition-colors">
              Retour à la connexion →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-amber-500" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">Bienvenue dans l'équipe !</h2>
            <p className="text-sm text-slate-400">Redirection en cours…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
          <span className="font-bold text-2xl tracking-tight text-white">
            Revert<span className="text-amber-500">AI</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center justify-center mb-5">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Users size={24} className="text-amber-500" />
            </div>
          </div>

          <h1 className="text-xl font-semibold text-white text-center mb-1">
            Vous avez été invité
          </h1>
          {orgName && (
            <p className="text-center text-slate-400 text-sm mb-6">
              Rejoignez l'organisation <strong className="text-white">{orgName}</strong> sur RevertAI
            </p>
          )}
          {inviteEmail && (
            <p className="text-center text-xs text-slate-500 mb-6">
              Invitation pour <span className="text-slate-300">{inviteEmail}</span>
            </p>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-950/50 border border-red-800 rounded-lg px-4 py-3 mb-5 text-red-400 text-sm">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          {session && emailMismatch ? (
            // Une session traîne sur un autre compte — cas courant quand on
            // enchaîne les invitations depuis le même navigateur. `accept_invite`
            // refuserait de toute façon : autant le dire avant le clic.
            <>
              <div className="flex items-start gap-2 bg-amber-950/40 border border-amber-700/50 rounded-lg px-4 py-3 mb-4 text-amber-300 text-sm">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>
                  Vous êtes connecté avec <strong className="text-amber-200">{session.user.email}</strong>,
                  alors que cette invitation est destinée à{' '}
                  <strong className="text-amber-200">{inviteEmail}</strong>.
                </span>
              </div>
              <button
                onClick={handleSwitchAccount}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                Me déconnecter et rejoindre avec {inviteEmail}
              </button>
            </>
          ) : session ? (
            // Already logged in → accept directly
            <button
              onClick={handleAccept}
              disabled={status === 'accepting'}
              className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
            >
              {status === 'accepting' ? 'Acceptation…' : "Rejoindre l'organisation"}
            </button>
          ) : mode === 'code' ? (
            // Le code ouvre la session — et crée le compte s'il n'existe pas
            // encore, ce qui est le cas normal d'un invité depuis que
            // l'invitation ne crée plus de compte à l'avance. L'adresse est
            // imposée par l'invitation, donc rien à inventer ici.
            <EmailCodeForm
              initialEmail={inviteEmail ?? ''}
              lockEmail={!!inviteEmail}
              allowCreateUser
              onSuccess={acceptWithSession}
              onCancel={() => setMode('choose')}
              cancelLabel="Revenir aux autres options"
            />
          ) : (
            // Not logged in → offer signup/login
            <>
              <div className="space-y-2 mb-4" onClick={handleOAuthWithInvite}>
                <GoogleButton label="Rejoindre avec Google" />
                {/* TODO: Activer quand la connexion LinkedIn est implémentée — bouton retiré, il menait vers un fournisseur inactif */}
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-slate-700" />
                <span className="text-xs text-slate-500">ou</span>
                <div className="flex-1 h-px bg-slate-700" />
              </div>

              {/* Un seul chemin par email : le code. Il vaut vérification de
                  l'adresse, il traverse les protections de messagerie
                  d'entreprise, et le mot de passe se choisit juste après, à
                  l'écran où l'on saisit son nom. */}
              <button
                onClick={() => setMode('code')}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
              >
                Recevoir un code par email
              </button>

              <p className="text-center text-sm text-slate-500 mt-4">
                Déjà un compte ?{' '}
                <Link
                  to={`/login?invite=${token}`}
                  onClick={handleOAuthWithInvite}
                  className="text-amber-500 hover:text-amber-400 font-medium transition-colors"
                >
                  Se connecter
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
